/**
 * Status text for one location target, shared by the dashboard's Targets
 * card, the location list's target progress tooltips, the chart markers and
 * the event timeline, so they all say the same thing:
 *  - reached: when, in the pour's timezone, and how long after the concrete
 *    start (maturity counted from), as legacy measured it; the monitoring
 *    start stands in when the location has no concrete start;
 *  - not reached: the maturity still to go. There is no time-to-target
 *    prediction, only the maturity.
 *
 * Pure functions only (no React, no API calls).
 */
import type { MaturityDescriptor } from '../services/pours';
import { formatDateTimeWithZone, parseUtc } from './datetime';
import type { IsoLike } from './datetime';
import { formatMaturity, maturityShortLabel, roundHalfUp } from './maturityDisplay';

/** A span as "1d 2h 5m" / "1h 30m" (whole minutes, rounded down); a negative
 *  span shows as "0h 0m". */
export const formatElapsed = (ms: number): string => {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  return days > 0 ? `${days}d ${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
};

/** Epoch ms of a backend time (naive UTC), an epoch ms or a Date; null when
 *  empty or invalid. */
const toMs = (value: IsoLike): number | null => {
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * How long after the location's start `at` was: "20h 0m after concrete
 * start", or "after monitoring start" when there is no concrete start. Null
 * when neither start is known, and when `at` is before the start (legacy
 * data stamped before its concrete start): no elapsed text then.
 */
export const elapsedSinceStart = (
  at: IsoLike,
  concreteStart?: IsoLike,
  monitoringStart?: IsoLike,
): string | null => {
  const time = toMs(at);
  if (time == null) return null;
  const concrete = toMs(concreteStart);
  const start = concrete ?? toMs(monitoringStart);
  if (start == null || time < start) return null;
  return `${formatElapsed(time - start)} after ${concrete != null ? 'concrete' : 'monitoring'} start`;
};

/**
 * An amount of maturity, half up to 1 dp with its unit: "143.5 °C·hrs". An
 * equivalent age names its index, "12.0 hrs (EA at 20 °C)", so it never
 * reads as clock hours. An amount above 0 that rounds to 0.0 is "under 0.1".
 */
export const maturityAmountText = (value: number, meta: MaturityDescriptor): string => {
  const text =
    value > 0 && roundHalfUp(value, 1) === 0 ? `under ${formatMaturity(0.1, meta)}` : formatMaturity(value, meta);
  return meta.kind === 'ea' ? `${text} (${maturityShortLabel(meta)})` : text;
};

/** reached: stamped by the engine; pending: maturity still to go; passed:
 *  maturity is past the target but no stamp yet; short: monitoring ended
 *  before the target was reached. */
export type TargetStatusKind = 'reached' | 'pending' | 'passed' | 'short';

export interface TargetStatus {
  text: string;
  kind: TargetStatusKind;
}

export interface TargetStatusInput {
  /** The engine's maturity stamp: the only rule for whether a target is met. */
  achieved: boolean;
  /** When it was stamped (naive UTC, epoch ms or Date). */
  achievedAt?: IsoLike;
  /** The target's maturity threshold (TTF or equivalent age). */
  targetMaturity?: number | null;
  /** The location's current maturity; missing counts as 0. */
  currentMaturity?: number | null;
  /** When maturity is counted from (the location's EAStart). */
  concreteStart?: IsoLike;
  /** When the location's logging started. */
  monitoringStart?: IsoLike;
  /** Monitoring has ended: the pour is complete or the location's logging
   *  ended. */
  ended?: boolean;
  /** The pour's maturity naming. */
  meta: MaturityDescriptor;
  /** The pour's IANA timezone; UTC when missing. */
  timezoneIana?: string | null;
}

/**
 * One status line for a target:
 *  - "Reached 01 Oct 2026, 12:30 AEST (20h 0m after concrete start)", the
 *    elapsed part from the monitoring start when there is no concrete start
 *    and left out when no start is known or the stamp is before it;
 *    "Reached (time not recorded)" without a stamp time;
 *  - "Not yet: 143.5 °C·hrs to go";
 *  - "Not yet recorded: maturity has passed this target" while the stamp is
 *    still to come;
 *  - "Not reached: 143.5 °C·hrs short when monitoring ended".
 */
export const targetStatusText = ({
  achieved,
  achievedAt,
  targetMaturity,
  currentMaturity,
  concreteStart,
  monitoringStart,
  ended,
  meta,
  timezoneIana,
}: TargetStatusInput): TargetStatus => {
  if (achieved) {
    const at = toMs(achievedAt);
    if (at == null) return { kind: 'reached', text: 'Reached (time not recorded)' };
    const when = formatDateTimeWithZone(at, timezoneIana);
    const elapsed = elapsedSinceStart(at, concreteStart, monitoringStart);
    return { kind: 'reached', text: elapsed ? `Reached ${when} (${elapsed})` : `Reached ${when}` };
  }
  const target = finite(targetMaturity);
  const current = finite(currentMaturity) ?? 0;
  const remaining = target != null && target > 0 ? target - current : null;
  if (remaining != null && remaining <= 0) {
    return { kind: 'passed', text: 'Not yet recorded: maturity has passed this target' };
  }
  const amount = remaining != null ? maturityAmountText(remaining, meta) : null;
  if (ended) {
    return {
      kind: 'short',
      text: amount ? `Not reached: ${amount} short when monitoring ended` : 'Not reached when monitoring ended',
    };
  }
  return { kind: 'pending', text: amount ? `Not yet: ${amount} to go` : 'Not yet' };
};
